import React from 'react';
import { colors } from '@material-ui/core';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs/Tabs';
import { makeStyles } from '@material-ui/styles';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { BLOCK_TYPES, BLOCK_TYPE_KEYS, chameleonElId } from '../../../lib/constants';
import { useIsMediumScreenMediaQuery } from '../../../lib/isSmallScreen';
import { createRandomId } from '../../../lib/randomId';
import { useShallowSelector, useDispatch } from '../../../lib/reduxHooks';
import { hex2rgba } from '../../../lib/utils';
import * as actions from '../../../store/senderBuild/actions';
import { type BlockType, type IBlock, type ISigner } from '../../../store/senderBuild/types';
import { MobileBlockSettings } from '../MobileBlockSettings';

const useStyles = makeStyles(
  () => ({
    tab: {
      color: colors.grey[800],
      fontSize: 16,
      opacity: 1,
      minWidth: '28%',
      borderRadius: 0,
      margin: 0,
      padding: '16px 12px 17px',
      textTransform: 'initial',
      fontWeight: 'normal',
      borderRight: `1px solid ${colors.grey[400]}`,
      '&:first-child': {
        borderLeft: `1px solid ${colors.grey[400]}`,
      },
      '&:hover:not($selected)': {
        backgroundColor: `${colors.grey[200]} !important`,
      },
    },
    tabs: {
      width: '100%',
      borderBottom: '1px solid #CAD5E0',
      height: '100%',
    },
    tabsIndicator: {
      display: 'none',
    },
    selected: {
      color: `${colors.grey[800]} !important`,
    },
  }),
  { name: 'MobileMarkupToolSelectionTabs' }
);

export function MobileMarkupToolSelectionTabs() {
  const dispatch = useDispatch();
  const classes = useStyles();
  const isMdScreenOrSmaller = useIsMediumScreenMediaQuery();

  const state = useShallowSelector((state) => ({
    resource: state.senderBuild.resource,
    settings: state.pageFrame.settings,
    currentPage: state.senderBuild.currentPage ?? 0,
    currentDocument: state.senderBuild.currentDocument ?? '',
    pageDimensions: state.senderBuild.pageDimensions,
    blockSizingCache: state.senderBuild.blockSizingCache,
    selectedBlocks: state.senderBuild.selectedBlocks,
    activeBlockType: state.senderBuild.activeBlockType,
    activeSigner: state.senderBuild.activeSigner,
    signers: state.senderBuild.signers,
  }));

  const disabledBlocks = state.settings?.account?.[state.resource!]?.blockTypes?.disabled ?? [];
  const blockTypeArray = BLOCK_TYPES.slice(1).filter((blockType) => !disabledBlocks.includes(blockType.key));

  const currentSigner =
    state.activeSigner === 'default'
      ? (state.signers.find((signer: ISigner) => signer.signerId === '1') as ISigner)
      : state.signers.find((signer: ISigner) => signer.signerId === state.activeSigner)!;

  function createMobileBlock(type: BlockType) {
    const { pageDimensions, currentDocument, currentPage } = state;

    const currentBlockType = BLOCK_TYPES.find((bt) => bt.key === type);
    let height;
    let width = 0;
    if (state.blockSizingCache[type] && type !== BLOCK_TYPE_KEYS.STRIKE) {
      height = state.blockSizingCache[type].height;
      width = state.blockSizingCache[type].width;
    } else {
      height = currentBlockType?.size?.default.height;
      width = currentBlockType?.size?.default.width ?? 0;
    }

    let x: number = pageDimensions[currentDocument][currentPage].width / 2;
    let y: number = pageDimensions[currentDocument][currentPage].height / 2;

    if (x + width > pageDimensions[currentDocument][currentPage].width) {
      // get diff that the block is over
      // subtract that from x
      const overlapDiffX = x + width - pageDimensions[currentDocument][currentPage].width;
      x -= overlapDiffX;
    }

    // @ts-expect-error -- This code was written prior to enforcing lint rules
    if (y + height > pageDimensions[currentDocument][currentPage].height) {
      // get diff that the block is over
      // subtract that from y

      // @ts-expect-error -- This code was written prior to enforcing lint rules
      const overlapDiffY = y + height - pageDimensions[currentDocument][currentPage].height;
      y -= overlapDiffY;
    }

    const isStrikeBlock = type === BLOCK_TYPE_KEYS.STRIKE;

    const newBlock: IBlock = {
      blockId: createRandomId(),
      blockType: type,
      pageNumber: state.currentPage,
      x,
      y,
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      fontSize: currentBlockType.size.fontSize,
      width: isStrikeBlock ? 80 : width,
      height,
      required: true,
      assignedTo: isStrikeBlock ? null : state.activeSigner,
      isEditingDisabled: false,
    };

    dispatch(actions.createNewBlock(state.currentDocument, state.currentPage, newBlock, true));

    if (type === BLOCK_TYPE_KEYS.TEXT) {
      dispatch(actions.enableTextEditing());
    }
  }

  if (!isMdScreenOrSmaller) return null;
  if (state.selectedBlocks.blockIndices.length) return <MobileBlockSettings />;

  return (
    <Tabs
      classes={{ root: classes.tabs, indicator: classes.tabsIndicator }}
      value={state.activeBlockType || false}
      variant="scrollable"
      scrollButtons="off"
      data-spec="blockTabs"
      style={{ overflowY: 'hidden' }}
      id={chameleonElId(4)}
    >
      {blockTypeArray
        .filter((b) => b.key !== BLOCK_TYPE_KEYS.RECTANGLE)
        .map((type) => {
          const selected = state.activeBlockType === type.key;
          const backgroundColor = selected ? hex2rgba(currentSigner.color!, 0.1) : 'inherit';
          const boxShadow = selected ? `0 3px 0 0 ${currentSigner.color} inset` : 'none';

          return (
            <Tab
              key={type.key}
              style={{ backgroundColor, boxShadow }}
              classes={{ root: classes.tab, selected: classes.selected }}
              className="flex-1 border-none"
              icon={type.icon}
              label={type.name}
              value={type.key}
              disableRipple
              data-spec={`block-tab-${type.key}`}
              tabIndex={0}
              onClick={() => {
                dispatch(actions.changeActiveBlockType(type.key));
                createMobileBlock(type.key);
              }}
            />
          );
        })}
    </Tabs>
  );
}
